import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TFunction } from 'i18next'
import { LabelKeyObject } from 'react-csv/lib/core'
import { UnitTypes } from 'src/Libs/types'

type UseCsvLink<U> = { csvData: U[]; csvHeaders: LabelKeyObject[] }

export type LabelConfig<U> = {
  [key in keyof U]: {
    translationKey: string
    unit?: UnitTypes
  }
}

/**
 * This hook is used to generate CSV data and headers for a given dataset.
 * It takes four parameters:
 *
 * @param data: The original array of data objects to be converted into CSV format.
 * @param labelConfig: An object that maps keys in the data objects to their corresponding translation keys and unit getters.
 * @param mappingFunction: A function that maps a data object to the desired CSV data format.
 * @param view (optional): A parameter that can be used by the unit getters in the labelConfig to determine the appropriate unit.
 *
 * The hook returns an object containing two properties:
 * - csvData: An array of data objects in the desired CSV format.
 * - csvHeaders: An array of objects, each containing a key from the data objects and its corresponding label for the CSV file.
 *
 */
export function useCsvLink<T, U>(data: T[], labelConfig: LabelConfig<U>, mappingFunction: (d: T) => U): UseCsvLink<U> {
  const { t } = useCustomTranslation()

  const csvData = data.map(mappingFunction)
  const csvHeaders = Object.keys(labelConfig).map(key => ({ key, label: getTraceLabel(key, labelConfig, t) }))

  return { csvData, csvHeaders }
}

export function getTraceLabel<U>(key: string, labelConfig: LabelConfig<U>, t: TFunction): string {
  const label = labelConfig[key as keyof U]
  const unit = label?.unit ? ` [${label.unit}]` : ''

  return `${t(label.translationKey)}${unit}`
}
